import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Component, Inject, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer } from '@angular/platform-browser';
import { I18NService } from '@ebw-monorepo/i18n-translate';
import { LoginService } from '@ebw-monorepo/login';
import { EbwModalService } from '@ebw-monorepo/modal';
import { PERMISSION_NAME, PermissionService, toSafeImageBlobUrl } from '@ebw-monorepo/utils';
import { TranslateService } from '@ngx-translate/core';
import { catchError, map, throwError } from 'rxjs';
import { API_URLS, ApiUrlService } from '../../../../../../../utils/src/lib/services/api-url.service';
import { IProjection } from '../../../../interfaces/mapSettings.interface';
import { ShapeLayerModel, ShapeLayerStyleListModel } from '../../../../models/shape.model';
import { MapService } from '../../../../services/map.service';
import { safeJsonStringify } from '@ebw-monorepo/microservices-helpers/utils/parser';

@Component({
  selector: 'ebw-monorepo-add-shape-layer-form',
  templateUrl: './add-shape-layer-form.component.html',
  styleUrls: ['./add-shape-layer-form.component.scss']
})
export class ShapeLayerFormComponent implements OnInit {
  public shapeForm: FormGroup;
  public layerStyleOptions: ShapeLayerStyleListModel[] = [];
  public isEdit = false;
  public registeredProjections: IProjection[] = [];
  public selectedFileExtension: string;
  public publicPermission: boolean;
  public availableExtensions: string[] = [];

  constructor(
    private _sanitizer: DomSanitizer,
    private _httpClient: HttpClient,
    private _ebwModalService: EbwModalService,
    private _translateService: TranslateService,
    private _mapService: MapService,
    private _i18nService: I18NService,
    public dialogRef: MatDialogRef<ShapeLayerFormComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      layer?: {
        id: string;
        label: string;
        visible: boolean;
        owner: string;
        accessibility: any[];
        info: ShapeLayerModel;
      };
      layerStylesOptions: ShapeLayerStyleListModel[];
    },
    private _permissionService: PermissionService,
    private _apiUrlService: ApiUrlService
  ) {}

  ngOnInit(): void {
    if (this.data.layer && this.data.layer.info.filename) {
      this.isEdit = true;
    }

    this.publicPermission = this._permissionService.checkPermission(PERMISSION_NAME.MAP.SIDEBAR.VISIBILITY_FILE_LAYER);

    if (this._permissionService.checkPermission(PERMISSION_NAME.MAP.SHAPE.FILE_LAYER_CSV)) {
      this.availableExtensions.push('csv');
    }

    if (this._permissionService.checkPermission(PERMISSION_NAME.MAP.SHAPE.FILE_LAYER_SHP)) {
      this.availableExtensions.push('zip');
    }

    if (this._permissionService.checkPermission(PERMISSION_NAME.MAP.SHAPE.FILE_LAYER_KML)) {
      this.availableExtensions.push('kml');
    }

    if (this._permissionService.checkPermission(PERMISSION_NAME.MAP.SHAPE.FILE_LAYER_KMZ)) {
      this.availableExtensions.push('kmz');
    }

    const opts = {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + sessionStorage.getItem(LoginService.AUTH_TOKEN_KEY),
        user: sessionStorage.getItem(LoginService.USER_ID_KEY)
      })
    };

    this.layerStyleOptions = this.data.layerStylesOptions.map((el) => {
      const imageUrl = this._apiUrlService.createUrl(API_URLS.MAP.LAYER.GET_LEGEND);
      return {
        ...el,
        image$: this._httpClient
          .get(`${imageUrl}?style=${el.image}`, { ...opts, responseType: 'blob' })
          .pipe(map((val) => toSafeImageBlobUrl(this._sanitizer, val)))
      };
    });

    this.registeredProjections = this._mapService.registeredProjections;

    this.initForm(this.data.layer);
  }

  public initForm(
    data: {
      id: string;
      label: string;
      visible: boolean;
      owner: string;
      accessibility: any[];
      info: ShapeLayerModel;
    } = null
  ) {
    let accessibility = false;
    let label = null;

    if (data) {
      label = data.info.label;
      const isPrivate = data.accessibility.some((rel: any) => rel.user_id != null /*|| rel.group_id != null*/);

      accessibility = !isPrivate;
    }

    this.shapeForm = new FormBuilder().group({
      file: new FormControl(null, [Validators.required]),
      file_: new FormControl(null, [availableFileType(this.availableExtensions)]),
      label: new FormControl(data ? data.info.label : null, [
        Validators.required,
        Validators.pattern(/^(?!layer(_| )[0-9][0-9]*)/)
      ]),
      projection: new FormControl(null, [Validators.required]),
      notes: new FormControl(data ? data.info.notes : null, [Validators.required]),
      style: new FormControl(data ? data.info.style : null, [Validators.required]),
      accessibility: new FormControl(data ? accessibility : false)
    });

    // this.shapeForm.addControl('style', new FormControl((data) ? data.style : null));
  }

  public save() {
    if (this.shapeForm.invalid) {
      this.shapeForm.get('style').markAsTouched();
      this.shapeForm.get('file_').markAsTouched();
      this.shapeForm.get('file').markAsTouched();
      this.shapeForm.get('label').markAsTouched();
      this.shapeForm.get('notes').markAsTouched();
      this.shapeForm.get('projection').markAsTouched();
      this.shapeForm.updateValueAndValidity();

      return;
    }

    if (this.isEdit) {
      this._updateShape(this.shapeForm.value);
    } else {
      this._createShape(this.shapeForm.value);
    }
  }

  public onFileSelect(event) {
    if (event.target.files.length > 0) {
      const file = event.target.files[0];
      this.shapeForm.get('file').setValue(file);
      this.shapeForm.get('file_').setValue(file.name);

      let filenameComponents = file.name.split('.');
      this.selectedFileExtension = filenameComponents[filenameComponents.length - 1];

      if (
        this.selectedFileExtension === 'kmz' ||
        this.selectedFileExtension === 'kml' ||
        this.selectedFileExtension === 'xml'
      ) {
        this.shapeForm.controls['projection'].setValue('EPSG:4326');
      }
    }
  }

  public getDisplayName(projection: IProjection): string {
    let name = '';

    name += projection.code;

    if (projection.info) {
      if (projection.info.name) {
        name += ` ${projection.info.name}`;
      }
    }

    return name;
  }

  public getTooltipInfo(projection: IProjection): string {
    let info = '';

    if (!projection) {
      return info;
    }

    if (!projection.info) {
      return info;
    }

    if (projection.info.datum) {
      info += `${this._i18nService.getInstantValue('map.sidebar.shape.tooltips.datum')}: ${projection.info.datum}`;
    }

    if (projection.info.projection) {
      if (info.length !== 0) {
        info += '\n';
      }

      info += `${this._i18nService.getInstantValue('map.sidebar.shape.tooltips.projection')}: ${
        projection.info.projection
      }`;
    }

    if (projection.info.zone) {
      if (info.length !== 0) {
        info += '\n';
      }

      info += `${this._i18nService.getInstantValue('map.sidebar.shape.tooltips.zone')}: ${projection.info.zone}`;
    }

    return info;
  }

  public getSelectedTooltipInfo(code: string): string {
    let info = '';

    let foundElement = this.registeredProjections.find((el) => {
      return el.code === code;
    });

    if (!foundElement) {
      return info;
    }

    return this.getTooltipInfo(foundElement);
  }

  public getAvailableExtensions(): string {
    let formattedExtensions = [];

    for (let extension of this.availableExtensions) {
      formattedExtensions.push('.' + extension);
    }

    return formattedExtensions.join(',');
  }

  private _createShape(shape: ShapeLayerModel) {
    const formData = new FormData();
    // se privato metto la visibilità per l'utente
    // TODO gestire nel futuro casi di visibilità a gruppi (va modificata l'interfaccia per far scegliere i gruppi a cui l'utente appartiene)
    formData.append(
      'accessibility',
      !shape.accessibility
        ? safeJsonStringify({ user_id: sessionStorage.getItem(LoginService.USER_ID_KEY) }, '{}')
        : '{}'
    );
    formData.append('file', shape.file);
    formData.append('notes', shape.notes);
    formData.append('label', shape.label);
    formData.append('layerStyle', shape.style);
    formData.append('projection', shape.projection.split(':')[1]);

    const opts = {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + sessionStorage.getItem(LoginService.AUTH_TOKEN_KEY),
        user: sessionStorage.getItem(LoginService.USER_ID_KEY)
      })
    };

    this._httpClient
      .post(this._apiUrlService.createUrl(API_URLS.MAP.LAYER.ADD_LAYER_FILE), formData, opts)
      .pipe(
        catchError((error) => {
          return throwError(() => error);
        })
      )
      .subscribe({
        next: (res: any) => {
          this._ebwModalService.openSuccessModal({
            title: this._translateService.instant('map.sidebar.shape.load_success'),
            content: ''
          });

          this.dialogRef.close('RELOAD');
        },
        error: (err) => {
          let content = '';

          if (err && err.message) {
            if (err.error.message.toLowerCase() == 'layer already exists') {
              content = this._translateService.instant('map.sidebar.shape.layer_already_exists');
            } else {
              content = err.error.message;
            }
          } else {
            content = this._translateService.instant('common.data_retrieving_error');
          }

          this._ebwModalService.openErrorModal({
            content: content,
            title: this._translateService.instant('common.error')
          });
        }
      });
  }

  private _updateShape(shape: ShapeLayerModel) {
    const formData = new FormData();
    formData.append(
      'accessibility',
      !shape.accessibility
        ? safeJsonStringify({ user_id: sessionStorage.getItem(LoginService.USER_ID_KEY) }, '{}')
        : '{}'
    );
    formData.append('file', shape.file);
    formData.append('notes', shape.notes);
    formData.append('label', shape.label);
    formData.append('layerStyle', shape.style);
    formData.append('projection', shape.projection.split(':')[1]);
    formData.append('id', this.data.layer.info.id.toString());

    // if (!shape.visibility) {
    //   formData.append('destPrivate', 'true');
    // }

    // if (!this._initVisibility) {
    //   formData.append('private', 'true');
    // }

    const opts = {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + sessionStorage.getItem(LoginService.AUTH_TOKEN_KEY),
        user: sessionStorage.getItem(LoginService.USER_ID_KEY)
      })
    };

    this._httpClient
      .put(this._apiUrlService.createUrl(API_URLS.MAP.LAYER.UPDATE_LAYER), formData, opts)
      .pipe(
        catchError((error) => {
          return throwError(() => error);
        })
      )
      .subscribe({
        next: (res: any) => {
          this._ebwModalService.openSuccessModal({
            title: this._translateService.instant('map.sidebar.shape.update_success'),
            content: ''
          });

          this.dialogRef.close('RELOAD');
        },
        error: (err) => {
          this._ebwModalService.openErrorModal({
            content: this._translateService.instant('common.data_retrieving_error'),
            title: this._translateService.instant('common.error')
          });
        }
      });
  }
}

function availableFileType(availableTypes?: string[]): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }

    const filename = control.value;

    if (!filename) {
      return { noInputFile: true };
    }

    if (availableTypes) {
      if (availableTypes.includes(filename.split('.').at(-1))) {
        return null;
      }

      return { unsupportedFile: true };
    }

    return null;
  };
}
